import { useTranslation } from 'react-i18next';
import format from 'date-fns/format';

import type { ApiNabanDeedDetails } from '@izimi/api';
import { useDateLocale } from '@izimi/hooks';

import { useNabanStoreSetGlossaryModalKey } from '../store';

type Props = {
  deedDetails?: ApiNabanDeedDetails;
};

type Item = { title: string; value: string; onTitleInfoClick?: VoidFunction };

export const useNabanDeedDetailsItems = ({ deedDetails }: Props): Array<Item> => {
  const {
    t,
    i18n: { language },
  } = useTranslation('naban');
  const locale = useDateLocale(language);

  const setGlossaryModalKey = useNabanStoreSetGlossaryModalKey();

  if (deedDetails == null) return [];

  const interveningNotaries = deedDetails.interveningNotaries.reduce((acc, person, i) => {
    const fullName = `${person.firstNames} ${person.lastName}`;

    if (i === 0) {
      return fullName;
    }

    return `${acc}, ${fullName}`;
  }, '');

  return [
    deedDetails.deedReference
      ? {
          title: t('deed.details.reference.title'),
          value: deedDetails.deedReference,
        }
      : null,
    deedDetails.deedRepertoriumNumber
      ? {
          title: t('deed.details.repertoryNumber.title'),
          value: deedDetails.deedRepertoriumNumber,
        }
      : null,
    {
      title: t('deed.details.deedDate.title'),
      value: format(new Date(deedDetails.deedDate), 'PPP', {
        locale,
      }),
    },
    {
      title: t('deed.details.deedType.title'),
      value: deedDetails.deedType.label,
    },
    deedDetails.currentOrganizationOwner.name
      ? {
          title: t('deed.details.minuteHolderOffice.title'),
          value: deedDetails.currentOrganizationOwner.name,
          onTitleInfoClick: () => setGlossaryModalKey('minuteHolderOffice'),
        }
      : null,
    deedDetails.minuteHolder.firstNames && deedDetails.minuteHolder.lastName
      ? {
          title: t('deed.details.minuteHolder.title'),
          value: `${deedDetails.minuteHolder.firstNames} ${deedDetails.minuteHolder.lastName}`,
          onTitleInfoClick: () => setGlossaryModalKey('minuteHolder'),
        }
      : null,
    deedDetails.originalOrganizationOwner.name
      ? {
          title: t('deed.details.instrumentingOffice.title'),
          value: deedDetails.originalOrganizationOwner.name,
          onTitleInfoClick: () => setGlossaryModalKey('instrumentingOffice'),
        }
      : null,
    deedDetails.penHolder.firstNames && deedDetails.penHolder.lastName
      ? {
          title: t('deed.details.penHolder.title'),
          value: `${deedDetails.penHolder.firstNames} ${deedDetails.penHolder.lastName}`,
          onTitleInfoClick: () => setGlossaryModalKey('penHolder'),
        }
      : null,
    interveningNotaries
      ? {
          title: t('deed.details.interveningNotary.title'),
          value: interveningNotaries,
          onTitleInfoClick: () => setGlossaryModalKey('interveningNotaries'),
        }
      : null,
  ].filter((item): item is Item => Boolean(item));
};
